import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import { PATIENT_CORE_FIELDS, PATIENT_FORM_STEPS, type PatientFormStep } from '@helios/domain';
import {
  qPatientFieldDefinitionOverrides,
  buildPatientFormSettingsVM,
  qAppConfig,
  getPatientFormLayoutMode,
  parseIdTypeOptions,
  writeIdTypeOptions,
  PREDEFINED_ID_TYPES,
  type PatientFieldDefinitionRow,
  type EffectivePatientField,
  type AppConfigRow,
  type PatientFormLayoutMode,
} from '@helios/data-access';
import type { FieldValidation, Rule } from '@helios/validation';
import { ValidationControls } from '../../components/ValidationControls';

// ─── Local override state (UI-only, not persisted) ───

type LocalOverride = {
  label?: string;
  visible?: boolean;
  required?: boolean;
  width?: 'half' | 'full';
  order?: number;
  validation_override?: string | null;
};

// ─── Section group ───

type SectionGroup = {
  step: PatientFormStep;
  visible: EffectivePatientField[];
  hidden: EffectivePatientField[];
};

// ─── Step chip colors ───

const STEP_STYLES: Record<PatientFormStep, { bg: string; color: string }> = {
  'Personal Info': { bg: 'var(--color-info-bg)', color: 'var(--color-info)' },
  Contact: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  'Emergency Contact': { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' },
  Medication: { bg: 'var(--color-danger-bg)', color: 'var(--color-danger-text)' },
  'Intake Form': { bg: 'var(--border)', color: 'var(--text-muted)' },
};

// ─── Icons ───

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function GripIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="9" cy="6" r="1.5" />
      <circle cx="15" cy="6" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="9" cy="18" r="1.5" />
      <circle cx="15" cy="18" r="1.5" />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function ChevronIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transition: 'transform 150ms ease',
        transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ─── Toggle ───

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onChange}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: disabled ? 'var(--border)' : checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 150ms ease',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          transition: 'transform 150ms ease',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          display: 'block',
        }}
      />
    </button>
  );
}

// ─── Step Chip ───

function StepChip({ step }: { step: PatientFormStep }) {
  const s = STEP_STYLES[step];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '22px',
        letterSpacing: '0.12px',
        whiteSpace: 'nowrap',
      }}
    >
      {step}
    </span>
  );
}

// ─── Locked Chip ───

function LockedChip() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        padding: '0 6px',
        borderRadius: 'var(--radius-full)',
        background: 'var(--color-danger-bg)',
        color: 'var(--color-danger-text)',
        fontSize: 11,
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
        letterSpacing: '0.12px',
        whiteSpace: 'nowrap',
      }}
    >
      Locked
    </span>
  );
}

// ─── Filter Chip ───

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 30,
        padding: '0 12px',
        borderRadius: 'var(--radius-full)',
        border: active ? 'none' : '1px solid var(--border)',
        background: active ? 'var(--color-primary)' : 'transparent',
        color: active ? '#ffffff' : 'var(--text-muted)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        fontFamily: 'var(--font-family)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 150ms ease',
      }}
    >
      {label}
    </button>
  );
}

// ─── Confirm Dialog ───

function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          padding: 'var(--sp-6)',
          width: 400,
          maxWidth: '90vw',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            color: 'var(--text)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-base)',
            lineHeight: 'var(--line-height-body)',
            color: 'var(--text-muted)',
            marginTop: 'var(--sp-2)',
          }}
        >
          {body}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--sp-3)',
            marginTop: 'var(--sp-6)',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              height: 36,
              padding: '0 var(--sp-4)',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              height: 36,
              padding: '0 var(--sp-4)',
              borderRadius: 'var(--input-radius)',
              border: 'none',
              background: 'var(--color-danger)',
              color: '#ffffff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Label Edit Modal ───

function LabelEditModal({
  open,
  fieldKey,
  currentLabel,
  baseLabel,
  onSave,
  onCancel,
}: {
  open: boolean;
  fieldKey: string;
  currentLabel: string;
  baseLabel: string;
  onSave: (key: string, newLabel: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(currentLabel);

  // Sync when modal opens for a different field
  const [prevKey, setPrevKey] = useState(fieldKey);
  if (fieldKey !== prevKey) {
    setPrevKey(fieldKey);
    setValue(currentLabel);
  }

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          padding: 'var(--sp-6)',
          width: 400,
          maxWidth: '90vw',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            color: 'var(--text)',
          }}
        >
          Edit Field Label
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            marginTop: 'var(--sp-1)',
          }}
        >
          Default: {baseLabel}
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          style={{
            width: '100%',
            height: 40,
            padding: '0 12px',
            marginTop: 'var(--sp-4)',
            border: '1px solid var(--border-input)',
            borderRadius: 'var(--input-radius)',
            background: 'var(--input-bg)',
            color: 'var(--input-text)',
            fontSize: 'var(--font-size-base)',
            fontFamily: 'var(--font-family)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSave(fieldKey, value);
            if (e.key === 'Escape') onCancel();
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--sp-3)',
            marginTop: 'var(--sp-4)',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              height: 36,
              padding: '0 var(--sp-4)',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <Button variant="gradient" size="sm" onClick={() => onSave(fieldKey, value)}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Toast ───

function Toast({ message, visible }: { message: string; visible: boolean }) {
  if (!visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        background: 'var(--text)',
        color: 'var(--bg-surface)',
        padding: '10px 20px',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      }}
    >
      {message}
    </div>
  );
}

// ─── Grid template (added 24px grip column) ───

const COL_TEMPLATE = '24px 1fr 120px 80px 80px 80px 160px';

// ─── Field Validation Panel (expanded row) ───

function mapToValidationType(fieldType: string | undefined): string {
  switch (fieldType) {
    case 'email':
    case 'tel':
    case 'textarea':
      return 'text';
    default:
      return fieldType ?? 'text';
  }
}

function parseFieldValidation(raw: string | null): FieldValidation | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.rules)) return parsed as FieldValidation;
  } catch {
    /* ignore invalid */
  }
  return undefined;
}

function FieldValidationPanel({
  field,
  onUpdateOverride,
}: {
  field: EffectivePatientField;
  onUpdateOverride: (jsonStr: string | null) => void;
}) {
  const vcType = mapToValidationType(field.field_type);
  const fv = parseFieldValidation(field.validation_override);
  const jsonPreview = fv && fv.rules.length > 0 ? JSON.stringify(fv, null, 2) : '';

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-6)',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <div style={{ maxWidth: 360 }}>
        <ValidationControls
          question={{ fieldKey: field.field_key, type: vcType }}
          fieldValidation={fv}
          onUpdate={(_fieldKey: string, fieldType: string, rules: Rule[]) => {
            const newFv: FieldValidation = {
              type: fieldType as FieldValidation['type'],
              rules,
            };
            onUpdateOverride(rules.length > 0 ? JSON.stringify(newFv) : null);
          }}
        />
        {jsonPreview && (
          <details
            style={{
              marginTop: 'var(--sp-3)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            <summary style={{ cursor: 'pointer' }}>JSON Preview</summary>
            <pre
              style={{
                margin: '8px 0 0',
                padding: 'var(--sp-3)',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--input-radius)',
                fontSize: 'var(--font-size-sm)',
                lineHeight: '1.5',
                overflow: 'auto',
                whiteSpace: 'pre',
                maxHeight: 160,
              }}
            >
              {jsonPreview}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

// ─── Country Picker Config Panel (for mobileCountryCode field) ───

const ALL_COUNTRY_CODES = [
  'AF',
  'AL',
  'DZ',
  'AD',
  'AO',
  'AG',
  'AR',
  'AM',
  'AU',
  'AT',
  'AZ',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'BE',
  'BZ',
  'BJ',
  'BT',
  'BO',
  'BA',
  'BW',
  'BR',
  'BN',
  'BG',
  'BF',
  'BI',
  'KH',
  'CM',
  'CA',
  'CV',
  'CF',
  'TD',
  'CL',
  'CN',
  'CO',
  'KM',
  'CG',
  'CR',
  'HR',
  'CU',
  'CY',
  'CZ',
  'DK',
  'DJ',
  'DM',
  'DO',
  'EC',
  'EG',
  'SV',
  'GQ',
  'ER',
  'EE',
  'SZ',
  'ET',
  'FJ',
  'FI',
  'FR',
  'GA',
  'GM',
  'GE',
  'DE',
  'GH',
  'GR',
  'GD',
  'GT',
  'GN',
  'GW',
  'GY',
  'HT',
  'HN',
  'HU',
  'IS',
  'IN',
  'ID',
  'IR',
  'IQ',
  'IE',
  'IL',
  'IT',
  'JM',
  'JP',
  'JO',
  'KZ',
  'KE',
  'KI',
  'KW',
  'KG',
  'LA',
  'LV',
  'LB',
  'LS',
  'LR',
  'LY',
  'LI',
  'LT',
  'LU',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MT',
  'MH',
  'MR',
  'MU',
  'MX',
  'FM',
  'MD',
  'MC',
  'MN',
  'ME',
  'MA',
  'MZ',
  'MM',
  'NA',
  'NR',
  'NP',
  'NL',
  'NZ',
  'NI',
  'NE',
  'NG',
  'KP',
  'MK',
  'NO',
  'OM',
  'PK',
  'PW',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PL',
  'PT',
  'QA',
  'RO',
  'RU',
  'RW',
  'KN',
  'LC',
  'VC',
  'WS',
  'SM',
  'ST',
  'SA',
  'SN',
  'RS',
  'SC',
  'SL',
  'SG',
  'SK',
  'SI',
  'SB',
  'SO',
  'ZA',
  'KR',
  'SS',
  'ES',
  'LK',
  'SD',
  'SR',
  'SE',
  'CH',
  'SY',
  'TW',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TO',
  'TT',
  'TN',
  'TR',
  'TM',
  'TV',
  'UG',
  'UA',
  'AE',
  'GB',
  'US',
  'UY',
  'UZ',
  'VU',
  'VE',
  'VN',
  'YE',
  'ZM',
  'ZW',
];

function CountryPickerConfigPanel({
  validationOverride,
  onUpdate,
}: {
  validationOverride: string | null;
  onUpdate: (jsonStr: string | null) => void;
}) {
  // Parse existing config
  const config = useMemo(() => {
    if (!validationOverride)
      return { countries: ['PH', 'SG', 'MY', 'TH', 'ID', 'VN'], defaultCountry: 'PH' };
    try {
      const parsed = JSON.parse(validationOverride);
      const cp = parsed?.countryPicker;
      if (cp && Array.isArray(cp.countries) && cp.countries.length > 0) {
        return {
          countries: cp.countries as string[],
          defaultCountry: (cp.defaultCountry as string) || cp.countries[0],
        };
      }
    } catch {
      /* ignore */
    }
    return { countries: ['PH', 'SG', 'MY', 'TH', 'ID', 'VN'], defaultCountry: 'PH' };
  }, [validationOverride]);

  const [selectedCountries, setSelectedCountries] = useState<string[]>(config.countries);
  const [defaultCountry, setDefaultCountry] = useState<string>(config.defaultCountry);
  const [searchQuery, setSearchQuery] = useState('');

  // Sync when config changes externally
  useEffect(() => {
    setSelectedCountries(config.countries);
    setDefaultCountry(config.defaultCountry);
  }, [config.countries.join(','), config.defaultCountry]);

  const getLabel = (iso2: string) => {
    try {
      const dn = new Intl.DisplayNames([navigator.language], { type: 'region' });
      return dn.of(iso2) ?? iso2;
    } catch {
      return iso2;
    }
  };

  const filteredAll = useMemo(() => {
    if (!searchQuery.trim()) return ALL_COUNTRY_CODES;
    const q = searchQuery.toLowerCase();
    return ALL_COUNTRY_CODES.filter((c) => {
      const name = getLabel(c).toLowerCase();
      return name.includes(q) || c.toLowerCase().includes(q);
    });
  }, [searchQuery]);

  const persist = (countries: string[], defCountry: string) => {
    // Merge with existing validation_override (preserve other keys like rules)
    let existing: Record<string, unknown> = {};
    if (validationOverride) {
      try {
        existing = JSON.parse(validationOverride);
      } catch {
        /* ignore */
      }
    }
    existing.countryPicker = { countries, defaultCountry: defCountry };
    onUpdate(JSON.stringify(existing));
  };

  const toggleCountry = (iso2: string) => {
    let next: string[];
    if (selectedCountries.includes(iso2)) {
      next = selectedCountries.filter((c) => c !== iso2);
      if (next.length === 0) return; // must have at least one
    } else {
      next = [...selectedCountries, iso2];
    }
    let newDefault = defaultCountry;
    if (!next.includes(defaultCountry)) {
      newDefault = next[0];
    }
    setSelectedCountries(next);
    setDefaultCountry(newDefault);
    persist(next, newDefault);
  };

  const handleDefaultChange = (iso2: string) => {
    setDefaultCountry(iso2);
    persist(selectedCountries, iso2);
  };

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-6)',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <div style={{ maxWidth: 400 }}>
        <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 12 }}>
          Country Picker Configuration
        </div>

        {/* Default country selector */}
        <div style={{ marginBottom: 12 }}>
          <label
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              display: 'block',
              marginBottom: 4,
            }}
          >
            Default Country
          </label>
          <select
            value={defaultCountry}
            onChange={(e) => handleDefaultChange(e.target.value)}
            style={{
              width: '100%',
              height: 36,
              padding: '0 12px',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'inherit',
            }}
          >
            {selectedCountries.map((c) => (
              <option key={c} value={c}>
                {getLabel(c)} ({c})
              </option>
            ))}
          </select>
        </div>

        {/* Searchable multi-select for supported countries */}
        <div style={{ marginBottom: 8 }}>
          <label
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              display: 'block',
              marginBottom: 4,
            }}
          >
            Supported Countries ({selectedCountries.length} selected)
          </label>

          {/* Selected countries chip bar */}
          {selectedCountries.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginBottom: 8,
                padding: 8,
                border: '1px solid var(--border)',
                borderRadius: 'var(--input-radius)',
                background: 'var(--bg-surface)',
                minHeight: 32,
              }}
            >
              {selectedCountries.map((c) => (
                <span
                  key={c}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    height: 24,
                    padding: '0 8px',
                    borderRadius: 'var(--radius-full)',
                    background:
                      defaultCountry === c ? 'var(--color-primary)' : 'var(--color-info-bg)',
                    color: defaultCountry === c ? '#fff' : 'var(--color-info)',
                    fontSize: 12,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getLabel(c)} ({c})
                  {selectedCountries.length > 1 && (
                    <button
                      onClick={() => toggleCountry(c)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 14,
                        height: 14,
                        border: 'none',
                        background: 'transparent',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: 14,
                        lineHeight: 1,
                        opacity: 0.7,
                      }}
                      title={`Remove ${getLabel(c)}`}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search countries to add..."
            style={{
              width: '100%',
              height: 32,
              padding: '0 12px',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'inherit',
              marginBottom: 8,
              boxSizing: 'border-box',
            }}
          />
          <div
            style={{
              maxHeight: 180,
              overflow: 'auto',
              border: '1px solid var(--border)',
              borderRadius: 'var(--input-radius)',
              background: 'var(--bg-surface)',
            }}
          >
            {filteredAll.map((c) => {
              const checked = selectedCountries.includes(c);
              return (
                <label
                  key={c}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 12px',
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                    background: checked ? 'var(--color-info-bg)' : 'transparent',
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleCountry(c)} />
                  {getLabel(c)} ({c})
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ID Type options (PRD: configurable picklist for the idType field) ───

function IdTypeOptionsPanel({
  validationOverride,
  onUpdate,
}: {
  validationOverride: string | null;
  onUpdate: (jsonStr: string | null) => void;
}) {
  const config = useMemo(() => parseIdTypeOptions(validationOverride), [validationOverride]);
  const [types, setTypes] = useState<string[]>(config.types);
  const [customDraft, setCustomDraft] = useState('');

  useEffect(() => {
    setTypes(config.types);
  }, [config.types.join('|')]);

  const persist = (next: string[]) => {
    onUpdate(writeIdTypeOptions(validationOverride, { types: next }));
  };

  const togglePreset = (label: string) => {
    const next = types.includes(label)
      ? types.filter((t) => t !== label)
      : [...types, label];
    if (next.length === 0) return; // never let the picklist go empty
    setTypes(next);
    persist(next);
  };

  const removeType = (label: string) => {
    const next = types.filter((t) => t !== label);
    if (next.length === 0) return;
    setTypes(next);
    persist(next);
  };

  const addCustom = () => {
    const trimmed = customDraft.trim();
    if (!trimmed) return;
    if (types.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      setCustomDraft('');
      return;
    }
    const next = [...types, trimmed];
    setTypes(next);
    persist(next);
    setCustomDraft('');
  };

  const move = (label: string, dir: -1 | 1) => {
    const idx = types.indexOf(label);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= types.length) return;
    const next = [...types];
    [next[idx], next[target]] = [next[target], next[idx]];
    setTypes(next);
    persist(next);
  };

  // Catalog rows = predefined union of types so users see what's available;
  // current selection drives the toggle state.
  const catalog = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of PREDEFINED_ID_TYPES) {
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
    for (const t of types) {
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
    return out;
  }, [types]);

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-6)',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <div style={{ maxWidth: 480 }}>
        <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 4 }}>
          ID Type Options
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            marginBottom: 12,
          }}
        >
          Toggle predefined types and add your own. The order here is the order shown in the
          dropdown.
        </div>

        {/* Selected list with reorder + remove */}
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--input-radius)',
            background: 'var(--bg-surface)',
            marginBottom: 12,
            overflow: 'hidden',
          }}
        >
          {types.map((t, idx) => (
            <div
              key={t}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderBottom:
                  idx < types.length - 1 ? '1px solid var(--border)' : 'none',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <span style={{ flex: 1 }}>{t}</span>
              <button
                type="button"
                onClick={() => move(t, -1)}
                disabled={idx === 0}
                title="Move up"
                style={iconBtnStyle(idx === 0)}
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(t, 1)}
                disabled={idx === types.length - 1}
                title="Move down"
                style={iconBtnStyle(idx === types.length - 1)}
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => removeType(t)}
                disabled={types.length <= 1}
                title={types.length <= 1 ? 'At least one type is required' : 'Remove'}
                style={iconBtnStyle(types.length <= 1)}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {/* Predefined toggles */}
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              marginBottom: 4,
            }}
          >
            Predefined types
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {catalog.map((t) => {
              const checked = types.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => togglePreset(t)}
                  style={{
                    height: 28,
                    padding: '0 10px',
                    borderRadius: 'var(--radius-full)',
                    border: checked
                      ? '1px solid var(--color-primary)'
                      : '1px solid var(--border)',
                    background: checked ? 'var(--color-info-bg)' : 'var(--bg-surface)',
                    color: checked ? 'var(--color-info)' : 'var(--text)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: checked ? 600 : 500,
                    cursor: 'pointer',
                  }}
                >
                  {checked ? '✓ ' : '+ '}
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom add */}
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              marginBottom: 4,
            }}
          >
            Add custom type
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              placeholder="e.g. Senior Citizen ID"
              value={customDraft}
              onChange={(e) => setCustomDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustom();
                }
              }}
              style={{
                flex: 1,
                height: 32,
                padding: '0 10px',
                borderRadius: 'var(--input-radius)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text)',
                outline: 'none',
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={addCustom}
              disabled={!customDraft.trim()}
            >
              Add
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function iconBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    width: 24,
    height: 24,
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    color: disabled ? 'var(--text-disabled)' : 'var(--text-muted)',
    fontSize: 13,
    lineHeight: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: 0,
  };
}

// ─── Main Page ───

export function PatientFormSettingsPage() {
  useOutletContext<{ collapsed: boolean }>();
  const { db } = usePowerSyncDb();

  const syncWriteEnabled = Boolean(import.meta.env.VITE_SYNCWRITE_URL);

  const [search, setSearch] = useState('');
  const [activeStep, setActiveStep] = useState<PatientFormStep | 'All'>('All');
  const [localOverrides, setLocalOverrides] = useState<Record<string, LocalOverride>>({});
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState<{
    key: string;
    label: string;
    baseLabel: string;
  } | null>(null);

  // Drag-and-drop state
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  // Hidden fields accordion state (expanded steps)
  const [hiddenExpanded, setHiddenExpanded] = useState<Record<string, boolean>>({});

  // Expanded field for validation editing
  const [expandedFieldKey, setExpandedFieldKey] = useState<string | null>(null);

  // Fetch DB overrides from PowerSync via TanStack Query
  const { data: dbOverrides } = usePowerSyncQuery<PatientFieldDefinitionRow>(
    qPatientFieldDefinitionOverrides(),
  );

  // Fetch app_config for layout mode
  const { data: appConfigRows } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const dbLayoutMode = useMemo(() => getPatientFormLayoutMode(appConfigRows?.[0]), [appConfigRows]);
  const [pendingLayoutMode, setPendingLayoutMode] = useState<PatientFormLayoutMode | null>(null);
  // Clear pending override once DB catches up
  useEffect(() => {
    setPendingLayoutMode(null);
  }, [dbLayoutMode]);
  const layoutMode = pendingLayoutMode ?? dbLayoutMode;
  const hasLayoutChange = pendingLayoutMode !== null && pendingLayoutMode !== dbLayoutMode;

  // Build the view model: code defaults + DB overrides
  const fields: EffectivePatientField[] = useMemo(
    () => buildPatientFormSettingsVM(PATIENT_CORE_FIELDS, dbOverrides ?? []),
    [dbOverrides],
  );

  // Clear local overrides once PowerSync query reactively updates with saved data
  useEffect(() => {
    setLocalOverrides({});
  }, [dbOverrides]);

  // Apply local UI overrides on top (not persisted)
  const resolvedFields = useMemo(() => {
    if (Object.keys(localOverrides).length === 0) return fields;
    return fields.map((f) => {
      const lo = localOverrides[f.field_key];
      if (!lo) return f;
      return {
        ...f,
        label: lo.label ?? f.label,
        visible: f.locked ? f.visible : (lo.visible ?? f.visible),
        required: f.locked && f.required ? f.required : (lo.required ?? f.required),
        width: lo.width ?? f.width,
        order: lo.order ?? f.order,
        validation_override:
          lo.validation_override !== undefined ? lo.validation_override : f.validation_override,
      };
    });
  }, [fields, localOverrides]);

  const hasOverrides = (dbOverrides?.length ?? 0) > 0;

  // Group fields by step → visible/hidden, sorted by order, filtered by search
  const sectionGroups = useMemo<SectionGroup[]>(() => {
    const stepsToShow = activeStep === 'All' ? PATIENT_FORM_STEPS : [activeStep];
    const q = search.toLowerCase().trim();

    return stepsToShow
      .map((step) => {
        let stepFields = resolvedFields
          .filter((f) => f.step === step)
          .sort((a, b) => a.order - b.order);

        if (q) {
          stepFields = stepFields.filter(
            (f) => f.label.toLowerCase().includes(q) || f.field_key.toLowerCase().includes(q),
          );
        }

        return {
          step,
          visible: stepFields.filter((f) => f.visible),
          hidden: stepFields.filter((f) => !f.visible),
        };
      })
      .filter((g) => g.visible.length > 0 || g.hidden.length > 0);
  }, [resolvedFields, activeStep, search]);

  // Total shown count for footer
  const totalShown = useMemo(
    () => sectionGroups.reduce((s, g) => s + g.visible.length + g.hidden.length, 0),
    [sectionGroups],
  );

  // Counts
  const totalVisibleCount = useMemo(
    () => resolvedFields.filter((f) => f.visible).length,
    [resolvedFields],
  );
  const totalRequiredCount = useMemo(
    () => resolvedFields.filter((f) => f.required).length,
    [resolvedFields],
  );

  // Toggle visible (local UI override)
  const toggleVisible = useCallback(
    (key: string) => {
      const field = resolvedFields.find((f) => f.field_key === key);
      if (!field || field.locked) return;
      setLocalOverrides((prev) => ({
        ...prev,
        [key]: { ...prev[key], visible: !field.visible },
      }));
    },
    [resolvedFields],
  );

  // Toggle required (local UI override)
  const toggleRequired = useCallback(
    (key: string) => {
      const field = resolvedFields.find((f) => f.field_key === key);
      if (!field) return;
      if (field.locked && field.required) return;
      setLocalOverrides((prev) => ({
        ...prev,
        [key]: { ...prev[key], required: !field.required },
      }));
    },
    [resolvedFields],
  );

  // Toggle width (local UI override)
  const toggleWidth = useCallback(
    (key: string) => {
      const field = resolvedFields.find((f) => f.field_key === key);
      if (!field) return;
      const next = field.width === 'full' ? 'half' : 'full';
      setLocalOverrides((prev) => ({
        ...prev,
        [key]: { ...prev[key], width: next },
      }));
    },
    [resolvedFields],
  );

  // Edit label (local UI override)
  const handleLabelSave = useCallback((key: string, newLabel: string) => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    setLocalOverrides((prev) => ({
      ...prev,
      [key]: { ...prev[key], label: trimmed },
    }));
    setEditingField(null);
  }, []);

  // ─── Drag-and-drop handlers ───

  const isDragEnabled = !search.trim();

  const handleDragStart = useCallback((e: React.DragEvent, key: string) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedKey(key);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedKey(null);
    setDragOverKey(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, key: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverKey(key);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetKey: string, step: PatientFormStep) => {
      e.preventDefault();
      if (!draggedKey || draggedKey === targetKey) {
        setDraggedKey(null);
        setDragOverKey(null);
        return;
      }

      const group = sectionGroups.find((g) => g.step === step);
      if (!group) return;

      const ordered = [...group.visible];
      const fromIdx = ordered.findIndex((f) => f.field_key === draggedKey);
      const toIdx = ordered.findIndex((f) => f.field_key === targetKey);
      if (fromIdx === -1 || toIdx === -1) return;

      const [moved] = ordered.splice(fromIdx, 1);
      ordered.splice(toIdx, 0, moved);

      setLocalOverrides((prev) => {
        const updated = { ...prev };
        ordered.forEach((f, i) => {
          updated[f.field_key] = { ...updated[f.field_key], order: (i + 1) * 10 };
        });
        return updated;
      });

      setDraggedKey(null);
      setDragOverKey(null);
    },
    [draggedKey, sectionGroups],
  );

  // Drop at end of section (move dragged item to last position)
  const handleDropEnd = useCallback(
    (e: React.DragEvent, step: PatientFormStep) => {
      e.preventDefault();
      if (!draggedKey) return;

      const group = sectionGroups.find((g) => g.step === step);
      if (!group) return;

      const ordered = [...group.visible];
      const fromIdx = ordered.findIndex((f) => f.field_key === draggedKey);
      if (fromIdx === -1) return;

      const [moved] = ordered.splice(fromIdx, 1);
      ordered.push(moved);

      setLocalOverrides((prev) => {
        const updated = { ...prev };
        ordered.forEach((f, i) => {
          updated[f.field_key] = { ...updated[f.field_key], order: (i + 1) * 10 };
        });
        return updated;
      });

      setDraggedKey(null);
      setDragOverKey(null);
    },
    [draggedKey, sectionGroups],
  );

  // Toggle hidden fields accordion
  const toggleHiddenExpanded = useCallback((step: string) => {
    setHiddenExpanded((prev) => ({ ...prev, [step]: !prev[step] }));
  }, []);

  // Auto-hide toast after 2.5s
  useEffect(() => {
    if (!toastVisible) return;
    const t = setTimeout(() => setToastVisible(false), 2500);
    return () => clearTimeout(t);
  }, [toastVisible]);

  // Show toast helper
  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    setToastVisible(true);
  }, []);

  // Save local overrides to PowerSync SQLite
  const handleSave = useCallback(async () => {
    if (saving) return;
    if (!db) {
      showToast('Database not ready — please wait for PowerSync to connect');
      return;
    }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      for (const [fieldKey, ov] of Object.entries(localOverrides)) {
        const coreDef = PATIENT_CORE_FIELDS.find((f) => f.field_key === fieldKey);
        if (!coreDef) continue;

        // Check if a row already exists for this key
        const existing = await db.getAll<PatientFieldDefinitionRow>(
          `SELECT * FROM patient_field_definitions WHERE key = ?`,
          [fieldKey],
        );

        if (existing.length > 0) {
          // Only update columns that are in localOverrides; keep existing values for the rest
          const prev = existing[0];
          const defaultLabel = ov.label !== undefined ? ov.label : prev.default_label;
          const defaultEnabled =
            ov.visible !== undefined ? (ov.visible ? 1 : 0) : prev.default_enabled;
          const requiredOverride =
            ov.required !== undefined ? (ov.required ? 1 : 0) : prev.required_override;
          const orderOverride = ov.order !== undefined ? ov.order : prev.order_override;
          const widthOverride = ov.width !== undefined ? ov.width : prev.width_override;
          const validationOverride =
            ov.validation_override !== undefined
              ? ov.validation_override
              : prev.validation_override;
          await db.execute(
            `UPDATE patient_field_definitions
             SET default_label = ?, default_enabled = ?, required_override = ?, order_override = ?, width_override = ?, validation_override = ?, updated_at = ?
             WHERE key = ?`,
            [
              defaultLabel,
              defaultEnabled,
              requiredOverride,
              orderOverride,
              widthOverride,
              validationOverride,
              now,
              fieldKey,
            ],
          );
        } else {
          const defaultLabel = ov.label !== undefined ? ov.label : coreDef.label;
          const defaultEnabled = ov.visible !== undefined ? (ov.visible ? 1 : 0) : null;
          const requiredOverride = ov.required !== undefined ? (ov.required ? 1 : 0) : null;
          const orderOverride = ov.order ?? null;
          const widthOverride = ov.width ?? null;
          const validationOverride = ov.validation_override ?? null;
          await db.execute(
            `INSERT INTO patient_field_definitions (id, key, field_type, category, default_label, default_enabled, required_override, order_override, width_override, validation_override, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              crypto.randomUUID(),
              fieldKey,
              coreDef.type,
              coreDef.step,
              defaultLabel,
              defaultEnabled,
              requiredOverride,
              orderOverride,
              widthOverride,
              validationOverride,
              now,
            ],
          );
        }
      }
      // Save layout mode change to app_config if pending
      if (hasLayoutChange && pendingLayoutMode) {
        const configRows = await db.getAll<AppConfigRow>(
          `SELECT extra_json FROM app_config WHERE id = 'app' LIMIT 1`,
        );
        let parsed: Record<string, unknown> = {};
        if (configRows[0]?.extra_json) {
          try {
            parsed = JSON.parse(configRows[0].extra_json);
          } catch {
            /* ignore */
          }
        }
        if (pendingLayoutMode === 'wizard') {
          delete parsed.patient_form_layout_mode;
        } else {
          parsed.patient_form_layout_mode = pendingLayoutMode;
        }
        await db.execute(`UPDATE app_config SET extra_json = ? WHERE id = 'app'`, [
          JSON.stringify(parsed),
        ]);
      }

      // Verify the write by reading back
      const saved = await db.getAll<PatientFieldDefinitionRow>(
        `SELECT * FROM patient_field_definitions`,
      );
      console.log('[PatientFormSettings] Saved overrides in SQLite:', saved);

      // Debug: check if PowerSync created CRUD entries for upload
      const crud = await db.getAll('SELECT * FROM ps_crud');
      console.log('[PatientFormSettings] Pending CRUD entries:', crud);

      // Debug: check PowerSync sync status
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dbAny = db as any;
      console.log('[PatientFormSettings] DB currentStatus:', dbAny.currentStatus);
      console.log('[PatientFormSettings] DB connected:', dbAny.connected);
      console.log('[PatientFormSettings] DB syncStatus:', dbAny.syncStatus);

      // Don't clear localOverrides here — wait for dbOverrides to update via usePowerSyncQuery
      // so the UI doesn't flash stale values. Overrides are cleared in the useEffect below.
      showToast(`Changes saved to local database (${saved.length} override rows)`);
    } catch (err) {
      console.error('[PatientFormSettings] Save failed:', err);
      showToast('Save failed — check console for details');
    } finally {
      setSaving(false);
    }
  }, [db, saving, localOverrides, hasLayoutChange, pendingLayoutMode, showToast]);

  // Layout mode toggle (local state; persisted on Save Changes)
  const handleLayoutModeChange = useCallback((mode: PatientFormLayoutMode) => {
    setPendingLayoutMode(mode);
  }, []);

  // Reset: delete all local override rows from PowerSync SQLite
  const handleResetConfirm = useCallback(async () => {
    setConfirmResetOpen(false);
    if (!db) {
      setLocalOverrides({});
      return;
    }
    try {
      await db.execute(`DELETE FROM patient_field_definitions`);
      setLocalOverrides({});
      showToast('Reset to defaults');
    } catch (err) {
      console.error('[PatientFormSettings] Reset failed:', err);
      showToast('Reset failed — check console for details');
    }
  }, [db, showToast]);

  // ─── Render a single field row ───

  const renderFieldRow = (
    field: EffectivePatientField,
    step: PatientFormStep,
    isDraggable: boolean,
    dimmed: boolean,
  ) => {
    const coreDef = PATIENT_CORE_FIELDS.find((f) => f.field_key === field.field_key);
    const isDragging = draggedKey === field.field_key;
    const isDragTarget =
      isDraggable && dragOverKey === field.field_key && draggedKey !== field.field_key;

    return (
      <Fragment key={field.field_key}>
        <div
          draggable={isDraggable && isDragEnabled}
          onDragStart={isDraggable ? (e) => handleDragStart(e, field.field_key) : undefined}
          onDragEnd={isDraggable ? handleDragEnd : undefined}
          onDragOver={isDraggable ? (e) => handleDragOver(e, field.field_key) : undefined}
          onDrop={isDraggable ? (e) => handleDrop(e, field.field_key, step) : undefined}
          style={{
            display: 'grid',
            gridTemplateColumns: COL_TEMPLATE,
            padding: '0 var(--sp-6)',
            borderBottom: '1px solid var(--border)',
            borderTop: isDragTarget ? '2px solid var(--color-primary)' : undefined,
            height: 48,
            alignItems: 'center',
            opacity: isDragging ? 0.4 : dimmed ? 0.5 : 1,
            cursor: isDraggable && isDragEnabled ? 'grab' : undefined,
            transition: 'opacity 150ms ease',
          }}
        >
          {/* Grip handle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            {isDraggable && isDragEnabled && <GripIcon />}
          </div>

          {/* Field label + pencil + locked chip */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--text)',
                fontWeight: 'var(--font-weight-medium)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {field.label}
            </span>
            {field.locked && <LockedChip />}
            <button
              onClick={() =>
                setEditingField({
                  key: field.field_key,
                  label: field.label,
                  baseLabel: coreDef?.label ?? field.label,
                })
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                background: 'transparent',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface-2)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              <PencilIcon />
            </button>
          </div>

          {/* Step chip */}
          <StepChip step={field.step} />

          {/* Visible toggle */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Toggle
              checked={field.visible}
              onChange={() => toggleVisible(field.field_key)}
              disabled={field.locked}
            />
          </div>

          {/* Required toggle */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Toggle
              checked={field.required}
              onChange={() => toggleRequired(field.field_key)}
              disabled={field.locked && field.required}
            />
          </div>

          {/* Width toggle */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => toggleWidth(field.field_key)}
              style={{
                height: 24,
                padding: '0 8px',
                borderRadius: 'var(--radius-xs)',
                border: '1px solid var(--border)',
                background: field.width === 'full' ? 'var(--color-primary)' : 'var(--bg-surface)',
                color: field.width === 'full' ? '#ffffff' : 'var(--text-muted)',
                fontSize: 11,
                fontWeight: 'var(--font-weight-medium)',
                fontFamily: 'var(--font-family)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {field.width === 'full' ? 'Full' : '½'}
            </button>
          </div>

          {/* Notes + Validation expand */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {field.notes ?? ''}
            </span>
            {(field.field_key === 'mobileCountryCode' ||
              field.field_key === 'idType' ||
              field.field_type === 'text' ||
              field.field_type === 'email' ||
              field.field_type === 'tel' ||
              field.field_type === 'textarea' ||
              field.field_type === 'number' ||
              field.field_type === 'date') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedFieldKey((prev) =>
                    prev === field.field_key ? null : field.field_key,
                  );
                }}
                title={
                  field.field_key === 'mobileCountryCode'
                    ? 'Country picker config'
                    : field.field_key === 'idType'
                      ? 'ID type options'
                      : 'Validation rules'
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background:
                    expandedFieldKey === field.field_key
                      ? 'var(--color-primary)'
                      : field.validation_override
                        ? 'var(--color-info-bg)'
                        : 'transparent',
                  color:
                    expandedFieldKey === field.field_key
                      ? '#fff'
                      : field.validation_override
                        ? 'var(--color-info)'
                        : 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 0,
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <ShieldCheckIcon />
              </button>
            )}
          </div>
        </div>

        {/* Expanded config/validation panel */}
        {expandedFieldKey === field.field_key &&
          (field.field_key === 'mobileCountryCode' ? (
            <CountryPickerConfigPanel
              validationOverride={field.validation_override}
              onUpdate={(jsonStr) => {
                setLocalOverrides((prev) => ({
                  ...prev,
                  [field.field_key]: {
                    ...prev[field.field_key],
                    validation_override: jsonStr,
                  },
                }));
              }}
            />
          ) : field.field_key === 'idType' ? (
            <IdTypeOptionsPanel
              validationOverride={field.validation_override}
              onUpdate={(jsonStr) => {
                setLocalOverrides((prev) => ({
                  ...prev,
                  [field.field_key]: {
                    ...prev[field.field_key],
                    validation_override: jsonStr,
                  },
                }));
              }}
            />
          ) : (
            <FieldValidationPanel
              field={field}
              onUpdateOverride={(jsonStr) => {
                setLocalOverrides((prev) => ({
                  ...prev,
                  [field.field_key]: {
                    ...prev[field.field_key],
                    validation_override: jsonStr,
                  },
                }));
              }}
            />
          ))}
      </Fragment>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '20px var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl)',
              letterSpacing: '-0.44px',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Patient Form Settings
          </h1>
          <p
            style={{
              fontSize: 'var(--font-size-base)',
              lineHeight: 'var(--line-height-base)',
              color: 'var(--text-muted)',
              margin: '4px 0 0',
            }}
          >
            Customize field labels, visibility, ordering, and requirements
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            onClick={() => setConfirmResetOpen(true)}
            style={{
              height: 36,
              padding: '0 var(--sp-4)',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
            }}
          >
            Reset to Default
          </button>
          <Button
            variant="gradient"
            size="md"
            onClick={handleSave}
            disabled={(Object.keys(localOverrides).length === 0 && !hasLayoutChange) || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Sync status banner */}
      <div
        style={{
          padding: 'var(--sp-2) var(--sp-6)',
          background: syncWriteEnabled ? 'var(--color-info-bg)' : 'var(--color-warning-bg)',
          color: syncWriteEnabled ? 'var(--color-info)' : 'var(--color-warning-text)',
          fontSize: 'var(--font-size-sm)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        {syncWriteEnabled
          ? 'Changes are saved locally and will sync to the server.'
          : 'Local-only mode: changes are saved to this browser and will sync once syncWrite is enabled.'}
      </div>

      {/* Info banner when using built-in defaults */}
      {!hasOverrides && (
        <div
          style={{
            padding: 'var(--sp-2) var(--sp-6)',
            background: 'var(--color-info-bg)',
            color: 'var(--color-info)',
            fontSize: 'var(--font-size-sm)',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          Using built-in defaults (no saved overrides).
        </div>
      )}

      {/* Layout Mode Toggle */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text)',
            }}
          >
            Form Layout
          </span>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              margin: '2px 0 0',
            }}
          >
            Choose how the Create Patient form is displayed
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 0,
            borderRadius: 'var(--input-radius)',
            overflow: 'hidden',
            border: '1px solid var(--border)',
          }}
        >
          {(['wizard', 'single_page'] as const).map((mode, i) => (
            <button
              key={mode}
              onClick={() => handleLayoutModeChange(mode)}
              style={{
                height: 34,
                padding: '0 16px',
                border: 'none',
                borderLeft: i > 0 ? '1px solid var(--border)' : 'none',
                background: layoutMode === mode ? 'var(--color-primary)' : 'var(--bg-surface)',
                color: layoutMode === mode ? '#ffffff' : 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                fontFamily: 'var(--font-family)',
                cursor: 'pointer',
              }}
            >
              {mode === 'wizard' ? 'Wizard' : 'Single Page'}
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar: search + filter chips */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--sp-4)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        {/* Search */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            height: 36,
            padding: '0 12px',
            border: '1px solid var(--border-input)',
            borderRadius: 'var(--input-radius)',
            background: 'var(--input-bg)',
            width: 240,
            flexShrink: 0,
          }}
        >
          <span style={{ color: 'var(--text-muted)', display: 'flex', flexShrink: 0 }}>
            <SearchIcon />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search fields..."
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
              fontFamily: 'var(--font-family)',
              outline: 'none',
              minWidth: 0,
            }}
          />
        </div>

        {/* Step filter chips */}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}
        >
          <FilterChip
            label="All"
            active={activeStep === 'All'}
            onClick={() => setActiveStep('All')}
          />
          {PATIENT_FORM_STEPS.map((step) => (
            <FilterChip
              key={step}
              label={step}
              active={activeStep === step}
              onClick={() => setActiveStep(step)}
            />
          ))}
        </div>
      </div>

      {/* Table header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COL_TEMPLATE,
          padding: '0 var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          height: 36,
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <span />
        <span style={colHeaderStyle}>Field Label</span>
        <span style={colHeaderStyle}>Step</span>
        <span style={{ ...colHeaderStyle, textAlign: 'center' }}>Visible</span>
        <span style={{ ...colHeaderStyle, textAlign: 'center' }}>Required</span>
        <span style={{ ...colHeaderStyle, textAlign: 'center' }}>Width</span>
        <span style={colHeaderStyle}>Notes</span>
      </div>

      {/* Section-grouped rows */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {sectionGroups.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-8) var(--sp-6)',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-base)',
            }}
          >
            No fields match your search.
          </div>
        ) : (
          sectionGroups.map(({ step, visible, hidden }) => (
            <div key={step}>
              {/* Section header */}
              <div
                style={{
                  padding: 'var(--sp-2) var(--sp-6)',
                  background: 'var(--bg-surface-2)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                }}
              >
                <StepChip step={step} />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {visible.length} visible{hidden.length > 0 ? `, ${hidden.length} hidden` : ''}
                </span>
              </div>

              {/* Visible fields (draggable) */}
              {visible.map((field) => renderFieldRow(field, step, true, false))}

              {/* End-of-list drop zone for dragging to last position */}
              {isDragEnabled && draggedKey && visible.length > 0 && (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverKey(`__end__:${step}`);
                  }}
                  onDrop={(e) => handleDropEnd(e, step)}
                  onDragLeave={() => {
                    setDragOverKey((prev) => (prev === `__end__:${step}` ? null : prev));
                  }}
                  style={{
                    height: 12,
                    borderTop:
                      dragOverKey === `__end__:${step}`
                        ? '2px solid var(--color-primary)'
                        : '2px solid transparent',
                    transition: 'border-color 100ms ease',
                  }}
                />
              )}

              {/* Hidden fields accordion */}
              {hidden.length > 0 && (
                <>
                  <button
                    onClick={() => toggleHiddenExpanded(step)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                      padding: 'var(--sp-2) var(--sp-6)',
                      border: 'none',
                      borderBottom: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      color: 'var(--text-muted)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      fontFamily: 'var(--font-family)',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <ChevronIcon collapsed={!hiddenExpanded[step]} />
                    {hidden.length} hidden field{hidden.length !== 1 ? 's' : ''}
                  </button>
                  {hiddenExpanded[step] &&
                    hidden.map((field) => renderFieldRow(field, step, false, true))}
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-6)',
          borderTop: '1px solid var(--border)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          Showing {totalShown} of {resolvedFields.length} fields &bull; {totalVisibleCount} visible
          &bull; {totalRequiredCount} required
        </span>
      </div>

      {/* Modals */}
      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset to default?"
        body="This will remove all custom settings and restore the default patient form fields."
        confirmLabel="Reset"
        onConfirm={handleResetConfirm}
        onCancel={() => setConfirmResetOpen(false)}
      />

      {editingField && (
        <LabelEditModal
          open
          fieldKey={editingField.key}
          currentLabel={editingField.label}
          baseLabel={editingField.baseLabel}
          onSave={handleLabelSave}
          onCancel={() => setEditingField(null)}
        />
      )}

      <Toast message={toastMessage} visible={toastVisible} />
    </div>
  );
}

// ─── Shared styles ───

const colHeaderStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)' as React.CSSProperties['fontWeight'],
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};
